import React, { useState } from "react";
import ReactCompareImage from "react-compare-image";
import "./App.css";

function App({ baseURL }) {
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [logoImage, setLogoImage] = useState(null);

  const [beforeImageFile, setBeforeImageFile] = useState(null);
  const [afterImageFile, setAfterImageFile] = useState(null);
  const [activeSidebar, setActiveSidebar] = useState(null); // 'content' | 'setting' etc.
  const [imageRatio, setImageRatio] = useState("3:2"); // default
  const [direction, setDirection] = useState("horizontal");
  const [showUserForm, setShowUserForm] = useState(false);
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userFormError, setUserFormError] = useState("");
  const [publishCount, setPublishCount] = useState(0);
  const [isPublishing, setIsPublishing] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadedURL, setUploadedURL] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [logoImageFile, setLogoImageFile] = useState(null);
  const [logoPosition, setLogoPosition] = useState({ top: 0, left: 0 }); // temp init
  const [isDragging, setIsDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [logoSize, setLogoSize] = useState(80); // width in px
  const [resizing, setResizing] = useState(false);

  const [shareEmail, setShareEmail] = useState("");
  const [shareName, setShareName] = useState("");
  const [shareSuccess, setShareSuccess] = useState(false);

  const handleShare = async () => {
    if (!shareEmail || !shareName) {
      alert("Please fill both fields.");
      return;
    }

    try {
      const res = await fetch(`${baseURL}/api/share`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          toEmail: shareEmail,
          toName: shareName,
          url: uploadedURL,
        }),
      });

      const result = await res.json();
      if (result.success) {
        setShareSuccess(true);
      } else {
        alert("Failed to send email.");
      }
    } catch (error) {
      console.error("Email send error", error);
      alert("Something went wrong.");
    }
  };

  const startResize = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setResizing(true);
  };
  const getAspectRatio = (value) => {
    switch (value) {
      case "1:1":
        return "1 / 1";
      case "3:2":
        return "3 / 2";
      case "2:3":
        return "2 / 3";
      case "4:3":
        return "4 / 3";
      case "3:4":
        return "3 / 4";
      case "16:9":
        return "16 / 9";
      case "9:16":
        return "9 / 16";
      default:
        return "3 / 2";
    }
  };

  const onResize = (e) => {
    if (!resizing) return;
    const clientX = e.clientX || e.touches[0].clientX;
    const wrapper = document.querySelector(".compare-wrapper");
    const rect = wrapper.getBoundingClientRect();
    const newWidth = clientX - rect.left - logoPosition.left;
    setLogoSize(Math.max(30, Math.min(newWidth, 300))); // Limit size
  };

  React.useEffect(() => {
    window.addEventListener("mousemove", onResize);
    window.addEventListener("mouseup", () => setResizing(false));
    window.addEventListener("touchmove", onResize);
    window.addEventListener("touchend", () => setResizing(false));
    return () => {
      window.removeEventListener("mousemove", onResize);
      window.removeEventListener("mouseup", () => setResizing(false));
      window.removeEventListener("touchmove", onResize);
      window.removeEventListener("touchend", () => setResizing(false));
    };
  }, [resizing, logoPosition]);

  const startDrag = (e) => {
    e.preventDefault();
    setIsDragging(true);

    const clientX = e.clientX || e.touches[0].clientX;
    const clientY = e.clientY || e.touches[0].clientY;

    const logo = e.target.getBoundingClientRect();
    setOffset({
      x: clientX - logo.left,
      y: clientY - logo.top,
    });
  };

  const onDrag = (e) => {
    if (!isDragging) return;

    const clientX = e.clientX || e.touches[0].clientX;
    const clientY = e.clientY || e.touches[0].clientY;

    const wrapper = document.querySelector(".compare-wrapper");
    const rect = wrapper.getBoundingClientRect();

    setLogoPosition({
      top: clientY - rect.top - offset.y,
      left: clientX - rect.left - offset.x,
    });
    // Snap range in pixels
    const snapThreshold = 30;
    const newTop = clientY - rect.top - offset.y;
    const newLeft = clientX - rect.left - offset.x;

    // Width and height of wrapper
    const wrapperWidth = wrapper.offsetWidth;
    const wrapperHeight = wrapper.offsetHeight;

    // Snap to corners
    const snappedTop =
      Math.abs(newTop) < snapThreshold
        ? 0
        : Math.abs(newTop - (wrapperHeight - logoSize)) < snapThreshold
        ? wrapperHeight - logoSize
        : newTop;

    const snappedLeft =
      Math.abs(newLeft) < snapThreshold
        ? 0
        : Math.abs(newLeft - (wrapperWidth - logoSize)) < snapThreshold
        ? wrapperWidth - logoSize
        : newLeft;

    setLogoPosition({
      top: snappedTop,
      left: snappedLeft,
    });
  };

  const stopDrag = () => {
    setIsDragging(false);
  };

  // Add event listeners
  React.useEffect(() => {
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", stopDrag);
    window.addEventListener("touchmove", onDrag);
    window.addEventListener("touchend", stopDrag);
    return () => {
      window.removeEventListener("mousemove", onDrag);
      window.removeEventListener("mouseup", stopDrag);
      window.removeEventListener("touchmove", onDrag);
      window.removeEventListener("touchend", stopDrag);
    };
  }, [isDragging, offset]);

  const handleImageChange = (e, setPreview, setFile) => {
    const file = e.target.files[0];
    if (file) {
      const imageURL = URL.createObjectURL(file);
      setPreview(imageURL);
      setFile(file);

      if (setPreview === setLogoImage) {
        setTimeout(() => {
          const wrapper = document.querySelector(".compare-wrapper");
          if (wrapper) {
            const wrapperWidth = wrapper.offsetWidth;
            const wrapperHeight = wrapper.offsetHeight;
            const defaultSize = 80; // logo width in px

            setLogoPosition({
              top: wrapperHeight - defaultSize - 20,
              left: wrapperWidth - defaultSize - 20,
            });
          }
        }, 100); // delay to ensure wrapper is mounted
      }
    }
  };

  const handlePublish = async () => {
    if (!beforeImageFile || !afterImageFile) {
      alert("Please upload both images");
      return;
    }

    setIsPublishing(true);
    setUploadSuccess(false);

    const formData = new FormData();
    formData.append("before", beforeImageFile);
    formData.append("after", afterImageFile);
    if (logoImageFile) {
      formData.append("logo", logoImageFile);
      const wrapper = document.querySelector(".compare-wrapper");
      const wrapperWidth = wrapper.offsetWidth;
      const wrapperHeight = wrapper.offsetHeight;

      formData.append(
        "logoPosition",
        JSON.stringify({
          top: `${(logoPosition.top / wrapperHeight) * 100}%`,
          left: `${(logoPosition.left / wrapperWidth) * 100}%`,
        })
      );

      // formData.append("logoPosition", JSON.stringify(logoPosition));
      formData.append("logoSize", logoSize);
    }
    formData.append("imageRatio", imageRatio);
    formData.append("direction", direction);

    try {
      const response = await fetch(`${baseURL}/api/upload`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.folderId) {
        const url = `https://vividlybuilt.com/${result.folderId}`;
        setUploadedURL(url);
        setUploadSuccess(true);
        setShowPopup(true);

        // ✅ fetch updated count
        await fetchPublishCount();

        // Copy to clipboard
        navigator.clipboard.writeText(url);
      }
    } catch (error) {
      alert("Something went wrong!");
    } finally {
      setIsPublishing(false);
    }
  };

  setTimeout(() => setUploadSuccess(false), 5000);

  const fetchPublishCount = async () => {
    try {
      const res = await fetch(`${baseURL}/api/stats`);
      const data = await res.json();
      setPublishCount(data.publishCount || 0);
    } catch (err) {
      console.error("Failed to fetch publish count:", err);
    }
  };
  React.useEffect(() => {
    fetchPublishCount();
  }, []);

  return (
    <div className="app">
      <header className="top-bar">
        <h1>Create Before and After Slider</h1>
        <div className="nav-center">
          <img src="/icons/logo.png" alt="Logo" className="nav-logo" />
        </div>
        <div
          className="action-buttons"
          style={{ display: "flex", alignItems: "center", gap: "20px" }}
        >
          <button
            className="publish"
            onClick={handlePublish}
            disabled={isPublishing}
          >
            {isPublishing ? "Publishing..." : "Publish"}
          </button>

          <button className="close" onClick={() => window.location.reload()}>
            Close
          </button>
        </div>
      </header>

      <div className="main-content">
        <aside className="sidebar">
          <div
            className={`sidebar-item ${
              activeSidebar === "content" ? "active" : ""
            }`}
            onClick={() => setActiveSidebar("content")}
          >
            <img src="/icons/content.svg" alt="Content" />
            <span>Content</span>
          </div>
          <div
            className={`sidebar-item ${
              activeSidebar === "setting" ? "active" : ""
            }`}
            onClick={() => setActiveSidebar("setting")}
          >
            <img src="/icons/setting.svg" alt="Setting" />
            <span>Setting</span>
          </div>
        </aside>

        <div className="left-panel">
          {/* ✅ Only show uploads if sidebar is not "setting" */}
          {activeSidebar !== "setting" && (
            <>
              {/* Upload: Before Photo */}
              <div className="upload-section">
                <label>Before Photo</label>
                {beforeImage && (
                  <div className="file-preview">
                    <img
                      src={beforeImage}
                      alt="Before"
                      className="preview-thumb"
                    />
                    <span className="file-name">before.jpg</span>
                  </div>
                )}
                <div className="custom-upload">
                  <input
                    type="file"
                    id="beforeUpload"
                    onChange={(e) =>
                      handleImageChange(e, setBeforeImage, setBeforeImageFile)
                    }
                    style={{ display: "none" }}
                  />
                  <label htmlFor="beforeUpload" className="upload-button">
                    <img src="/icons/upload-icon.svg" alt="Upload Icon" />
                    Add Photo
                  </label>
                </div>
              </div>

              {/* Upload: After Photo */}
              <div className="upload-section">
                <label>After Photo</label>
                {afterImage && (
                  <div className="file-preview">
                    <img
                      src={afterImage}
                      alt="After"
                      className="preview-thumb"
                    />
                    <span className="file-name">after.jpg</span>
                  </div>
                )}
                <div className="custom-upload">
                  <input
                    type="file"
                    id="afterUpload"
                    onChange={(e) =>
                      handleImageChange(e, setAfterImage, setAfterImageFile)
                    }
                    style={{ display: "none" }}
                  />
                  <label htmlFor="afterUpload" className="upload-button">
                    <img src="/icons/upload-icon.svg" alt="Upload Icon" />
                    Add Photo
                  </label>
                </div>
              </div>

              {/* Upload: Logo / Branding */}
              <div className="upload-section">
                <label>Branding</label>
                {logoImage && (
                  <div className="file-preview">
                    <img src={logoImage} alt="Logo" className="preview-thumb" />
                    <span className="file-name">your-logo.jpg</span>
                  </div>
                )}
                <div className="custom-upload">
                  <input
                    type="file"
                    id="logoUpload"
                    onChange={(e) =>
                      handleImageChange(e, setLogoImage, setLogoImageFile)
                    }
                    style={{ display: "none" }}
                  />
                  <label htmlFor="logoUpload" className="upload-button">
                    <img src="/icons/upload-icon.svg" alt="Upload Icon" />
                    Add Logo
                  </label>
                </div>
              </div>
            </>
          )}

          {/* ✅ Only show this if sidebar is "setting" */}
          {activeSidebar === "setting" && (
            <div className="setting-panel">
            <h3>Setting</h3>
          
            <div className="setting-item">
              <label>Image Ratio</label>
              <select
                value={imageRatio}
                onChange={(e) => setImageRatio(e.target.value)}
              >
                <option value="1:1">1:1 Square</option>
                <option value="3:2">3:2 Standard</option>
                <option value="2:3">2:3 Standard (Vertical)</option>
                <option value="4:3">4:3 Four-Three</option>
                <option value="3:4">3:4 Four-Three (Vertical)</option>
                <option value="16:9">16:9 Widescreen</option>
                <option value="9:16">9:16 Widescreen (Vertical)</option>
              </select>
            </div>
          
            <div className="setting-item">
              <label>Direction</label>
              <select
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
              >
                <option value="horizontal">Horizontal</option>
                <option value="vertical">Vertical</option>
              </select>
            </div>
          
            <p
              style={{
                color: "#ccc",
                fontSize: "14px",
                fontWeight: "500",
                margin: 0,
                whiteSpace: "nowrap",
                marginTop: "10px",
              }}
            >
              🔢 Total Published: <strong>{publishCount}</strong>
            </p>
          </div>
          
          )}
        </div>

        <div className="preview-area">
          {beforeImage && afterImage ? (
            <div
              className="preview-box"
              style={{ aspectRatio: getAspectRatio(imageRatio) }}
            >
              <div className="compare-wrapper">
                <div className="compare-inner">
                  <ReactCompareImage
                    leftImage={beforeImage}
                    rightImage={afterImage}
                    sliderPositionPercentage={0.5}
                    vertical={direction === "vertical"}
                    handle={
                      <img
                        src={
                          direction === "vertical"
                            ? "/icons/fav-ver.png"
                            : "/icons/fav.png"
                        }
                        alt="Slider Handle"
                        className="custom-handle"
                      />
                    }
                  />
                </div>

                {logoImage && (
                  <div
                    className="draggable-logo-wrapper"
                    style={{
                      top: `${logoPosition.top}px`,
                      left: `${logoPosition.left}px`,
                      width: `${logoSize}px`,
                    }}
                    onMouseDown={startDrag}
                    onTouchStart={startDrag}
                  >
                    <img
                      src={logoImage}
                      alt="Draggable Logo"
                      className="draggable-logo"
                    />
                    <div
                      className="resize-handle"
                      onMouseDown={startResize}
                    ></div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p style={{ color: "#aaa" }}>
              Please upload both Before and After photos to preview.
            </p>
          )}
        </div>
      </div>

      {/* ✅ Toast and popup go HERE (at the bottom of main layout) */}
      {uploadSuccess && (
        <div className="toast">
          ✅ Published successfully. URL copied to clipboard!
        </div>
      )}

      {showPopup && (
        <div className="popup">
          <div className="popup-content">
            <h2>Published URL</h2>
            <input type="text" value={uploadedURL} readOnly />
            <button onClick={() => navigator.clipboard.writeText(uploadedURL)}>
              Copy Again
            </button>
            <button
              onClick={() => {
                setShowPopup(false);
                setShareName("");
                setShareEmail("");
                setShareSuccess(false);
              }}
            >
              Close
            </button>

            <hr />
            <h3 style={{ marginTop: "20px" }}>
              📤 Share this slider with a friend
            </h3>

            <input
              type="text"
              placeholder="Friend's Name"
              value={shareName}
              onChange={(e) => setShareName(e.target.value)}
            />

            <input
              type="email"
              placeholder="Friend's Email"
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
            />

            <button onClick={handleShare}>Send Link via Email</button>

            {shareSuccess && (
              <p style={{ color: "green" }}>✅ Shared successfully!</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
